import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardPartConstantType {
    Torso,
    Target,
    Prop
}

function OperationString(operation : RewardPartConstantType) : string {
    if( typeof operation === 'number' )
        return RewardPartConstantType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardPartConstantType as any)[op];
}

/**
 * Block used to apply math functions
 */
export class RewardPartConstant extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Torso", value: RewardPartConstantType.Torso },
            { label: "Target", value: RewardPartConstantType.Target },
            { label: "Prop", value: RewardPartConstantType.Prop },
        ],
    })
    public property = RewardPartConstantType.Torso;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerOutput("part", RewardGraphConnectionPointTypes.PartID);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardPartConstant";
    }

    /**
     * Gets the left input component
     */
    public get part(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        switch (this.property) {
            case RewardPartConstantType.Torso: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getPartIndex("torso");
                        return 0;
                    };
                break;
            }
            case RewardPartConstantType.Target: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getPartIndex("target");
                        return -1;
                    };
                break;
            }
            case RewardPartConstantType.Prop: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getPartIndex("prop");
                        return -1;
                    };
                break;
            }
        }

        this.part._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardPartConstant.${RewardPartConstantType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();
        serializationObject.property = OperationString(this.property);
        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardPartConstant", RewardPartConstant);
